import type { Branding, Integrator } from '@designdeck/shared';
import {
  endImpersonationSession,
  isImpersonationTab,
} from '@/lib/impersonation-session';
import apiClient, { ApiResponse, tokenStorage } from './client';

// ═══════════════════════════════════════════════════════════════════
// TYPES
// ═══════════════════════════════════════════════════════════════════

export interface User {
  id: string;
  email: string;
  name: string;
  role: 'USER' | 'ADMIN' | 'CUSTOMER_ADMIN';
  customerId?: string;
  customer?: {
    id: string;
    name: string;
    slug: string;
    /** The customer's branding colours, see @designdeck/shared Branding. */
    branding?: Branding | null;
  };
  externalDesignId?: string;
  id_cloud: boolean;
  cloudSideRegistrationId?: number;
  createdAt: string;
}

export interface AuthResponse {
  accessToken: string;
  refreshToken: string;
  user: User;
}

export interface LoginDto {
  email: string;
  password: string;
}

// ═══════════════════════════════════════════════════════════════════
// API FUNCTIONS
// ═══════════════════════════════════════════════════════════════════

/**
 * Login with email and password
 */
export async function login(data: LoginDto): Promise<AuthResponse> {
  const response = await apiClient.post<ApiResponse<AuthResponse>>(
    '/auth/login',
    data
  );
  const authData = response.data.data;

  // Store tokens
  tokenStorage.setTokens(authData.accessToken, authData.refreshToken);

  return authData;
}

/**
 * Logout - clears tokens.
 *
 * In an impersonation tab it ends the impersonation instead: only the tab
 * storage is cleared and the tab reloads into the end screen. The admin's
 * session in localStorage stays, and there is no /login in that tab. No
 * server call either: a 401 on it would record the wrong end reason.
 */
export async function logout(): Promise<void> {
  if (isImpersonationTab()) {
    endImpersonationSession('ended');
    return;
  }
  try {
    await apiClient.post('/auth/logout');
  } catch {
    // Ignore errors, just clear tokens
  } finally {
    tokenStorage.clearTokens();
  }
}

/**
 * Get current user profile
 */
export async function getMe(): Promise<User> {
  const response = await apiClient.get<ApiResponse<User>>('/auth/me');
  return response.data.data;
}

/**
 * Login marketplace user inside an embedded iframe (HMAC-signed server call from Medusa).
 * The browser passes ts/sig query params; this function is called from App bootstrap.
 */
export async function loginIframe(
  userId: string,
  timestamp: string,
  signature: string,
  integrator: Integrator
): Promise<AuthResponse> {
  const response = await apiClient.post<ApiResponse<AuthResponse>>(
    '/auth/iframe-login',
    { userId },
    {
      headers: {
        'x-designdeck-registration-timestamp': timestamp,
        'x-designdeck-registration-signature': `sha256=${signature}`,
        'x-designdeck-integrator': integrator,
      },
    }
  );
  const authData = response.data.data;
  tokenStorage.setTokens(authData.accessToken, authData.refreshToken);
  return authData;
}

/**
 * Refresh tokens
 */
export async function refreshTokens(): Promise<{
  accessToken: string;
  refreshToken: string;
}> {
  const refreshToken = tokenStorage.getRefreshToken();
  if (!refreshToken) {
    throw new Error('No refresh token available');
  }

  const response = await apiClient.post<
    ApiResponse<{ accessToken: string; refreshToken: string }>
  >('/auth/refresh', { refreshToken });

  const tokens = response.data.data;
  tokenStorage.setTokens(tokens.accessToken, tokens.refreshToken);

  return tokens;
}

/**
 * Check if user is authenticated (has valid token)
 */
export function isAuthenticated(): boolean {
  return !!tokenStorage.getAccessToken();
}
